import enAccount from '../locales/en/account.json';
import enAlerts from '../locales/en/alerts.json';
import enBoats from '../locales/en/boats.json';
import enCalendar from '../locales/en/calendar.json';
import enCategories from '../locales/en/categories.json';
import enChecklists from '../locales/en/checklists.json';
import enCommon from '../locales/en/common.json';
import enCosts from '../locales/en/costs.json';
import enCustomFields from '../locales/en/customFields.json';
import enDashboard from '../locales/en/dashboard.json';
import enDomain from '../locales/en/domain.json';
import enErrors from '../locales/en/errors.json';
import enExport from '../locales/en/export.json';
import enFiles from '../locales/en/files.json';
import enImport from '../locales/en/import.json';
import enInventory from '../locales/en/inventory.json';
import enLogs from '../locales/en/logs.json';
import enOnboarding from '../locales/en/onboarding.json';
import enOrganizations from '../locales/en/organizations.json';
import enPlan from '../locales/en/plan.json';
import enPrint from '../locales/en/print.json';
import enQuickAdd from '../locales/en/quickAdd.json';
import enSearch from '../locales/en/search.json';
import enSettings from '../locales/en/settings.json';
import enSubscription from '../locales/en/subscription.json';
import enSupport from '../locales/en/support.json';
import enTasks from '../locales/en/tasks.json';
import enTrips from '../locales/en/trips.json';
import enUpdates from '../locales/en/updates.json';
import enValidation from '../locales/en/validation.json';
import itAccount from '../locales/it/account.json';
import itAlerts from '../locales/it/alerts.json';
import itBoats from '../locales/it/boats.json';
import itCalendar from '../locales/it/calendar.json';
import itCategories from '../locales/it/categories.json';
import itChecklists from '../locales/it/checklists.json';
import itCommon from '../locales/it/common.json';
import itCosts from '../locales/it/costs.json';
import itCustomFields from '../locales/it/customFields.json';
import itDashboard from '../locales/it/dashboard.json';
import itDomain from '../locales/it/domain.json';
import itErrors from '../locales/it/errors.json';
import itExport from '../locales/it/export.json';
import itFiles from '../locales/it/files.json';
import itImport from '../locales/it/import.json';
import itInventory from '../locales/it/inventory.json';
import itLogs from '../locales/it/logs.json';
import itOnboarding from '../locales/it/onboarding.json';
import itOrganizations from '../locales/it/organizations.json';
import itPlan from '../locales/it/plan.json';
import itPrint from '../locales/it/print.json';
import itQuickAdd from '../locales/it/quickAdd.json';
import itSearch from '../locales/it/search.json';
import itSettings from '../locales/it/settings.json';
import itSubscription from '../locales/it/subscription.json';
import itSupport from '../locales/it/support.json';
import itTasks from '../locales/it/tasks.json';
import itTrips from '../locales/it/trips.json';
import itUpdates from '../locales/it/updates.json';
import itValidation from '../locales/it/validation.json';
import type { Locale } from './config';

/**
 * The namespaces both apps share.
 *
 * - `common`: words that read the same on every screen of either app.
 * - `costs`: the costs feature — both apps' cost screens and forms, and the cost sheets other records open.
 * - `domain`: the vocabulary of shared option lists (`mdc-constants`) — engine types, log events.
 * - `alerts`: the alerts feature — both apps' alert lists, details, timelines and the mark-as-done flow.
 * - `validation`: Zod's built-in messages, and generic templates such as `maxLength`.
 * - `boats`: the boats feature — both apps' screens, and the copy `@marine/services` builds for them.
 * - `subscription`: the plan-limit copy `@marine/services` builds for both apps.
 * - `import`: the import modal — upload, field mapping, the boat step and the summary, worded per module.
 * - `print`: printing a list — the confirmation, the failure message and the sheet's header lines.
 * - `export`: the export modal — its formats, filters and states, worded per module.
 * - `search`: the global search — its box, module chips, result lines and keyboard hints.
 * - `plan`: choosing, changing and paying for the organization's plan — the plan, billing and checkout pages.
 * - `subscription`: the plan-limit copy `@marine/services` builds for both apps, and the sheets that show it.
 * - `subscription`: the plan-limit copy `@marine/services` builds for both apps.
 * - `quickAdd`: the quick-add menu — one entry per thing that can be created from anywhere.
 * - `support`: the help and support links — what each one is and where it goes.
 * - `errors`: records the backend rejected on sync — the banner on a form, and the list behind it.
 * - `subscription`: the plan-limit copy `@marine/services` builds for both apps.
 * - `updates`: telling the user a new version of the app is ready, or required.
 * - `calendar`: the calendar — what each kind of dated item is called, and an empty day.
 * - `subscription`: the plan-limit copy `@marine/services` builds for both apps.
 * - `customFields`: custom fields — the `@marine/custom-fields` package and both apps' manage screens.
 * - `settings`: the root of the settings area — the list of everything account-wide.
 * - `account`: the account's own screens — the preferences for units, formats, currency and time zone.
 * - `dashboard`: the home screen's widgets — overview counters, trips, costs, tasks and alerts.
 * - `onboarding`: the first-run setup — units, currency and the finished screen.
 * - `categories`: managing a module's custom categories, and the names shown for one without a name.
 * - `organizations`: the organization switcher, the members and invitations, and leaving an organization.
 * - `inventory`: the inventory feature — both apps' item screens and forms, and the locations items are stowed in.
 * - `checklists`: the checklists feature — both apps' own and public checklists, templates and copies.
 * - `logs`: the logbook feature — both apps' log screens, forms and their checks.
 * - `trips`: the trips feature — both apps' trip screens, the finish-trip flow and their checks.
 * - `subscription`: the plan-limit copy `@marine/services` builds for both apps.
 * - `tasks`: the tasks feature — both apps' task screens and forms, their statuses and priorities.
 * - `customFields`: the `@marine/custom-fields` package's own components, hooks and labels.
 * - `files`: the files feature — both apps' file browser, uploads, pickers and file details, and the rules `@marine/services` words for them.
 *
 * Screen copy lives here too, one namespace per feature, because the two apps build the same features
 * and say the same things on them: a string is written and translated once. Where an app genuinely
 * words something differently ("Save" on one, "Save task" on the other), that is a second key beside
 * the first, not a namespace of the app's own. An app keeps its own namespace only for copy no other
 * app has — see each app's `resources.ts`.
 */
export const sharedResources = {
  en: {
    common: enCommon,
    costs: enCosts,
    domain: enDomain,
    alerts: enAlerts,
    validation: enValidation,
    inventory: enInventory,
    boats: enBoats,
    errors: enErrors,
    calendar: enCalendar,
    settings: enSettings,
    account: enAccount,
    dashboard: enDashboard,
    onboarding: enOnboarding,
    categories: enCategories,
    organizations: enOrganizations,
    checklists: enChecklists,
    logs: enLogs,
    trips: enTrips,
    subscription: enSubscription,
    plan: enPlan,
    tasks: enTasks,
    customFields: enCustomFields,
    import: enImport,
    print: enPrint,
    export: enExport,
    search: enSearch,
    quickAdd: enQuickAdd,
    support: enSupport,
    updates: enUpdates,
    files: enFiles,
  },
  it: {
    common: itCommon,
    costs: itCosts,
    domain: itDomain,
    alerts: itAlerts,
    validation: itValidation,
    inventory: itInventory,
    boats: itBoats,
    errors: itErrors,
    calendar: itCalendar,
    settings: itSettings,
    account: itAccount,
    dashboard: itDashboard,
    onboarding: itOnboarding,
    categories: itCategories,
    organizations: itOrganizations,
    checklists: itChecklists,
    logs: itLogs,
    trips: itTrips,
    subscription: itSubscription,
    plan: itPlan,
    tasks: itTasks,
    customFields: itCustomFields,
    import: itImport,
    print: itPrint,
    export: itExport,
    search: itSearch,
    quickAdd: itQuickAdd,
    support: itSupport,
    updates: itUpdates,
    files: itFiles,
  },
} as const satisfies Record<Locale, Record<string, object>>;

/** The shape of the shared namespaces, for an app's `CustomTypeOptions['resources']`. */
export type SharedResources = (typeof sharedResources)['en'];

/** The namespace a bare key resolves in: `t('actions.save')` is `common:actions.save`. */
export const DEFAULT_NAMESPACE = 'common';
